import { z } from 'zod';

// ─── Enums ────────────────────────────────────────────────────────────────────

export const PageTypeEnum = z.enum(['third_party', 'custom_dashboard', 'dashboard', 'built_in_code']);
export type PageType = z.infer<typeof PageTypeEnum>;

// ─── Base Models ──────────────────────────────────────────────────────────────

/**
 * PageVersionSchema represents a saved snapshot of a dashboard config.
 * The `config` field holds a full DashboardConfig JSON object — validated
 * against DashboardConfigSchema (@trinity/chat) in the service layer.
 */
export const PageVersionSchema = z.object({
  id: z.number().int(),
  pageId: z.number().int(),
  versionNumber: z.number().int().min(1),
  /** Full DashboardConfig JSON snapshot */
  config: z.record(z.string(), z.unknown()),
  createdBy: z.number().nullable(),
  createdOn: z.coerce.date(),
  description: z.string().nullable(),
});
export type PageVersion = z.infer<typeof PageVersionSchema>;

export const PageSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  type: PageTypeEnum,
  dataConnector: z.string().nullable(),
  templateId: z.number().nullable(),
  currentVersionId: z.number().nullable(),
  /** For third_party and built_in_code pages: provider-specific page metadata. */
  providerData: z.record(z.string(), z.unknown()),
  isTemplate: z.boolean(),
  createdBy: z.number().nullable(),
  createdOn: z.coerce.date(),
  updatedBy: z.number(),
  updatedOn: z.coerce.date(),
});
export type Page = z.infer<typeof PageSchema>;

/** Page with the current version config inlined */
export const PageDetailSchema = PageSchema.extend({
  currentVersion: PageVersionSchema.nullable(),
});
export type PageDetail = z.infer<typeof PageDetailSchema>;

// ─── List Response ────────────────────────────────────────────────────────────

export const GetPagesResponseSchema = z.object({
  pages: z.array(PageDetailSchema),
});
export type GetPagesResponse = z.infer<typeof GetPagesResponseSchema>;

export const GetPageVersionsResponseSchema = z.object({
  versions: z.array(PageVersionSchema),
});
export type GetPageVersionsResponse = z.infer<typeof GetPageVersionsResponseSchema>;

// ─── Third-party provider data ────────────────────────────────────────────────

/** Plain iframe rendered with Figma-specific sizing (padding, calc height). */
export const FigmaProviderDataSchema = z.object({
  provider: z.literal('figma'),
  url: z.url(),
});
export type FigmaProviderData = z.infer<typeof FigmaProviderDataSchema>;

/**
 * Iframe cropped with absolute positioning to hide the external site's own
 * navigation bars. `topPadding` (px) fine-tunes the top crop offset.
 */
export const DmiProviderDataSchema = z.object({
  provider: z.literal('dmi'),
  url: z.url(),
  topPadding: z.number().int().default(20),
});
export type DmiProviderData = z.infer<typeof DmiProviderDataSchema>;

/** Rendered via the Tableau JS embedding API (`<tableau-viz>`). */
export const TableauProviderDataSchema = z.object({
  provider: z.literal('tableau'),
  url: z.url(),
});
export type TableauProviderData = z.infer<typeof TableauProviderDataSchema>;

/**
 * Allowlist for identifiers that end up in an embed URL (Displayr dashboard id, Sigma warehouse / schema / analysis
 * id). They are encoded when the URL is built; this rejects malformed values at the boundary as well.
 */
const EmbedIdentifierSchema = z
  .string()
  .trim()
  .min(1)
  .max(255)
  .regex(/^[A-Za-z0-9_$.-]+$/, 'Only letters, numbers and _ $ . - are allowed');

/** A Displayr dashboard, embedded as an iframe of `https://app.displayr.com/Dashboard?id=<dashboardId>`. */
export const DisplayrProviderDataSchema = z.object({
  provider: z.literal('displayr'),
  dashboardId: EmbedIdentifierSchema,
});
export type DisplayrProviderData = z.infer<typeof DisplayrProviderDataSchema>;

/** The Sigma dashboards a Sigma page can show, one per tab. The last three need `analysisId` (Patient Flow Analyzer). */
export const SigmaTabEnum = z.enum([
  'patient_count',
  'hcp_count',
  'hcp_universe',
  'hcp_profile',
  'treatment_claim_count',
  'epidemiology',
  'line_of_therapy',
  'compliance',
  'persistence',
]);
export type SigmaTab = z.infer<typeof SigmaTabEnum>;

/** Patient Flow Analyzer tabs, which read a per-analysis schema and so can't load without an analysis id. */
export const SIGMA_PFA_TABS: readonly SigmaTab[] = ['line_of_therapy', 'compliance', 'persistence'];

/**
 * A Sigma dashboard set. The embed URL is signed server-side per viewer and per tab, so the page stores only what
 * parameterises it: the warehouse and schema to query, and which optional tabs to show.
 */
export const SigmaProviderDataSchema = z.object({
  provider: z.literal('sigma'),
  warehouse: EmbedIdentifierSchema,
  schema: EmbedIdentifierSchema,
  /** Patient Flow Analyzer analysis; required for the line of therapy, compliance and persistence tabs. */
  analysisId: EmbedIdentifierSchema.optional(),
  includeTreatmentClaimCount: z.boolean().default(false),
  includeEpidemiology: z.boolean().default(false),
  includeLineOfTherapy: z.boolean().default(false),
  includeCompliance: z.boolean().default(false),
  includePersistence: z.boolean().default(false),
});
export type SigmaProviderData = z.infer<typeof SigmaProviderDataSchema>;

export const ThirdPartyProviderDataSchema = z.discriminatedUnion('provider', [
  FigmaProviderDataSchema,
  DmiProviderDataSchema,
  TableauProviderDataSchema,
  DisplayrProviderDataSchema,
  SigmaProviderDataSchema,
]);
export type ThirdPartyProviderData = z.infer<typeof ThirdPartyProviderDataSchema>;
